'use client';

import { Image, Label, RadioGroupItem, Typography } from '@musinsa/mds';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface ProductRadioItemProps {
  /** RadioGroup value (productId) */
  value: string;
  brand: string;
  productName: string;
  /** 표시용 가격 문자열 */
  price: string;
  imageUrl: string;
  imageAlt?: string;
  /** 썸네일 비율 — 29CM=square(5:5), 무신사=portrait(세로). 서비스별 상이. (SID-4427) */
  imageRatio?: 'square' | 'portrait';
  /** 품절(색상별) — 상세 상품 카드와 동일하게 dim+"품절" 표시 + 라디오 비선택. */
  soldOut?: boolean;
  className?: string;
}

/**
 * multi 모드 옵션 시트 step 1 — 같은 캠페인에 묶인 N개 상품 중 1개를 라디오로 선택하는 행.
 * 상위에서 `<RadioGroup value={...} onValueChange={...}>` 으로 감싼다.
 *
 * Figma 레이아웃 (좌→우): Radio · Image · (brand / productName / price 세로 stack)
 *
 * Radix RadioGroupItem 은 <button role="radio"> 로 렌더되어 label htmlFor 연관이
 * 동작하지 않는다. 카드 전체에 onClick 을 걸어 라디오 버튼을 programmatic click 한다.
 * 라디오 자체를 클릭하면 toggle 이 이미 발생하므로, 라디오 영역만 stopPropagation
 * 으로 카드 onClick 으로 버블링되어 두 번 토글되는 것을 막는다.
 */
export const ProductRadioItem = ({
  value,
  brand,
  productName,
  price,
  imageUrl,
  imageAlt,
  imageRatio = 'portrait',
  soldOut = false,
  className,
}: ProductRadioItemProps) => {
  const radioRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      role="presentation"
      // 품절은 비선택 — 카드 클릭으로 라디오를 토글하지 않는다.
      onClick={soldOut ? undefined : () => radioRef.current?.click()}
      className={twMerge(
        'flex items-center gap-[12px] px-[16px]! py-[8px]!',
        soldOut ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
    >
      <div className="flex items-center" onClick={(e) => e.stopPropagation()} role="presentation">
        <RadioGroupItem ref={radioRef} value={value} disabled={soldOut} />
      </div>
      {/* 썸네일 비율 — 29CM 5:5(72x72) / 무신사 세로(72x86). (SID-4427) */}
      <div
        className={twMerge(
          'relative w-[72px] rounded-[4px] overflow-hidden',
          imageRatio === 'square' ? 'h-[72px]' : 'h-[86px]',
        )}
      >
        <Image src={imageUrl} alt={imageAlt ?? productName} />
        {/* 상세 상품 카드와 동일 패턴 — mds Image wrapper(z-5) 위로 dim/라벨을 z-10 으로 올린다. */}
        {soldOut && (
          <>
            <div className="absolute inset-0 z-10 bg-black/20" />
            <div className="absolute bottom-[4px] left-[4px] z-10">
              <Label variant="darkGray">품절</Label>
            </div>
          </>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]!">
        <Typography variant="body13pxSemi" colorScheme={soldOut ? 'gray-600' : 'black'}>
          {brand}
        </Typography>
        <Typography
          variant="body13pxReg"
          colorScheme={soldOut ? 'gray-600' : 'gray-700'}
          className="line-clamp-2"
        >
          {productName}
        </Typography>
        <Typography
          variant="body13pxSemi"
          colorScheme={soldOut ? 'gray-400' : 'black'}
          className="mt-[4px]!"
        >
          {price}
        </Typography>
      </div>
    </div>
  );
};
